import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useI18n } from "../i18n/I18nProvider";
import { Loading } from "../components/StatusMessage";

/** Reloaded this tab this recently: show a link instead of reloading again. */
const RETRY_MS = 30_000;

/**
 * A page the server renders itself (/desarrolladores) that came up inside the app: a service worker
 * from before the page existed answered it with the app's shell. Updates the worker and loads the
 * page again, once; if that doesn't do it, a link.
 */
export function ServerPage({ reload = () => window.location.reload() }: { reload?: () => void }) {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const [stuck, setStuck] = useState(false);
  // The latest reload, without re-running the effect (and losing its timer) on every render.
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => {
    const key = `pf-server-page:${pathname}`;
    let last = 0;
    try {
      last = Number(window.sessionStorage.getItem(key) ?? 0);
    } catch {
      // No storage (private mode): the timer below still reloads, once per visit.
    }
    if (Date.now() - last < RETRY_MS) {
      setStuck(true);
      return;
    }
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      try {
        window.sessionStorage.setItem(key, String(Date.now()));
      } catch {
        // see above
      }
      reloadRef.current();
    };
    const sw = "serviceWorker" in navigator ? navigator.serviceWorker : null;
    if (!sw) return go();
    // The new worker takes over (skipWaiting) and no longer answers this page with the app.
    const timer = window.setTimeout(go, 4000);
    sw.addEventListener("controllerchange", go);
    void sw
      .getRegistration()
      .then((r) => r?.update())
      .catch(() => {});
    return () => {
      window.clearTimeout(timer);
      sw.removeEventListener("controllerchange", go);
    };
  }, [pathname]);
  return stuck ? (
    <div className="container">
      <p>
        <a href={pathname}>{t("serverPage.open")}</a>
      </p>
    </div>
  ) : (
    <Loading />
  );
}
